/**
 * FR7 — Derives the full 50-900 token set from the one colour a tenant picks.
 *
 * Generated in OKLCH (lightness, chroma, hue), not HSL: HSL's lightness steps
 * shift hue on saturated colours, so one set of stops would look right for some
 * hues and muddy for others.
 */

interface Rgb {
  red: number;
  green: number;
  blue: number;
}

interface Oklch {
  lightness: number;
  chroma: number;
  hue: number;
}

/** Oklab's rectangular form: the space OKLCH is the polar view of. */
interface Oklab {
  lightness: number;
  greenRedAxis: number;
  blueYellowAxis: number;
}

/** Long, medium and short cone responses — how the eye samples a colour. */
interface ConeResponse {
  long: number;
  medium: number;
  short: number;
}

interface RampStop {
  step: number;
  lightness: number;
  chromaScale: number;
}


const RAMP_STOPS: readonly RampStop[] = [
  { step: 50, lightness: 0.971, chromaScale: 0.16 },
  { step: 100, lightness: 0.936, chromaScale: 0.3 },
  { step: 200, lightness: 0.885, chromaScale: 0.5 },
  { step: 300, lightness: 0.808, chromaScale: 0.7 },
  { step: 400, lightness: 0.704, chromaScale: 0.88 },
  { step: 600, lightness: 0.487, chromaScale: 1 },
  { step: 700, lightness: 0.424, chromaScale: 0.94 },
  { step: 800, lightness: 0.371, chromaScale: 0.84 },
  { step: 900, lightness: 0.324, chromaScale: 0.72 },
];

const clampToUnit = (value: number) =>
  value < 0 ? 0 : value > 1 ? 1 : value;

export function hexToRgb(hex: string): Rgb | null {
  const value = hex.trim().replace(/^#/, '');
  const full =
    value.length === 3
      ? value
          .split('')
          .map((char) => char + char)
          .join('')
      : value;
  if (!/^[0-9a-f]{6}$/i.test(full)) return null;
  return {
    red: parseInt(full.slice(0, 2), 16) / 255,
    green: parseInt(full.slice(2, 4), 16) / 255,
    blue: parseInt(full.slice(4, 6), 16) / 255,
  };
}

function rgbToHex({ red, green, blue }: Rgb): string {
  const channel = (value: number) =>
    Math.round(clampToUnit(value) * 255)
      .toString(16)
      .padStart(2, '0');
  return `#${channel(red)}${channel(green)}${channel(blue)}`;
}

const gammaToLinear = (channel: number) =>
  channel <= 0.04045 ? channel / 12.92 : ((channel + 0.055) / 1.055) ** 2.4;

const linearToGamma = (channel: number) =>
  channel <= 0.0031308
    ? channel * 12.92
    : 1.055 * channel ** (1 / 2.4) - 0.055;

function rgbToConeResponse({ red, green, blue }: Rgb): ConeResponse {
  const linearRed = gammaToLinear(red);
  const linearGreen = gammaToLinear(green);
  const linearBlue = gammaToLinear(blue);

  return {
    long: Math.cbrt(
      0.4122214708 * linearRed +
        0.5363325363 * linearGreen +
        0.0514459929 * linearBlue,
    ),
    medium: Math.cbrt(
      0.2119034982 * linearRed +
        0.6806995451 * linearGreen +
        0.1073969566 * linearBlue,
    ),
    short: Math.cbrt(
      0.0883024619 * linearRed +
        0.2817188376 * linearGreen +
        0.6299787005 * linearBlue,
    ),
  };
}

function coneResponseToOklab({ long, medium, short }: ConeResponse): Oklab {
  return {
    lightness: 0.2104542553 * long + 0.793617785 * medium - 0.0040720468 * short,
    greenRedAxis: 1.9779984951 * long - 2.428592205 * medium + 0.4505937099 * short,
    blueYellowAxis:
      0.0259040371 * long + 0.7827717662 * medium - 0.808675766 * short,
  };
}

function rgbToOklch(rgb: Rgb): Oklch {
  const { lightness, greenRedAxis, blueYellowAxis } = coneResponseToOklab(
    rgbToConeResponse(rgb),
  );

  return {
    lightness,
    chroma: Math.hypot(greenRedAxis, blueYellowAxis),
    hue: (Math.atan2(blueYellowAxis, greenRedAxis) * 180) / Math.PI,
  };
}

function oklchToRgb({ lightness, chroma, hue }: Oklch): Rgb {
  const hueRadians = (hue * Math.PI) / 180;
  const greenRedAxis = chroma * Math.cos(hueRadians);
  const blueYellowAxis = chroma * Math.sin(hueRadians);

  const long =
    (lightness + 0.3963377774 * greenRedAxis + 0.2158037573 * blueYellowAxis) **
    3;
  const medium =
    (lightness - 0.1055613458 * greenRedAxis - 0.0638541728 * blueYellowAxis) **
    3;
  const short =
    (lightness - 0.0894841775 * greenRedAxis - 1.291485548 * blueYellowAxis) **
    3;

  return {
    red: linearToGamma(4.0767416621 * long - 3.3077115913 * medium + 0.2309699292 * short),
    green: linearToGamma(
      -1.2684380046 * long + 2.6097574011 * medium - 0.3413193965 * short,
    ),
    blue: linearToGamma(
      -0.0041960863 * long - 0.7034186147 * medium + 1.707614701 * short,
    ),
  };
}

/** The 50–900 steps for a base colour, keyed by step number. */
export function buildRamp(hex: string): Record<number, string> | null {
  const base = hexToRgb(hex);
  if (!base) return null;

  const { chroma, hue } = rgbToOklch(base);
  const ramp: Record<number, string> = {};
  for (const { step, lightness, chromaScale } of RAMP_STOPS) {
    ramp[step] = rgbToHex(
      oklchToRgb({ lightness, chroma: chroma * chromaScale, hue }),
    );
  }
  return ramp;
}

const relativeLuminance = ({ red, green, blue }: Rgb) =>
  0.2126 * gammaToLinear(red) +
  0.7152 * gammaToLinear(green) +
  0.0722 * gammaToLinear(blue);

const contrastRatio = (one: number, other: number) =>
  (Math.max(one, other) + 0.05) / (Math.min(one, other) + 0.05);

/** Otherwise white is hardcoded, and a tenant picking yellow gets white-on-yellow. */
export function readableForeground(hex: string): string {
  const rgb = hexToRgb(hex);
  if (!rgb) return '#ffffff';

  const own = relativeLuminance(rgb);
  const againstWhite = contrastRatio(own, 1);
  const againstBlack = contrastRatio(
    own,
    relativeLuminance({ red: 0.04, green: 0.04, blue: 0.04 }),
  );
  return againstWhite >= againstBlack ? '#ffffff' : '#0a0a0a';
}

/** A lighter sibling for the accent, matching the platform's own pairing. */
export function lighten(hex: string, amount = 0.12): string {
  const rgb = hexToRgb(hex);
  if (!rgb) return hex;
  const { lightness, chroma, hue } = rgbToOklch(rgb);
  return rgbToHex(
    oklchToRgb({ lightness: clampToUnit(lightness + amount), chroma, hue }),
  );
}
